import { api } from '@/lib/api';

export interface WhatsAppConfig {
  baseUrl: string;
  apiId: string;
  apiToken: string;
  webhookUrl: string;
}

export interface MessageTemplate {
  id: string;
  name: string;
  content: string;
  variables: string[];
  category: string;
  isActive: boolean;
}

export interface Contact {
  id: string;
  name: string;
  phone: string;
  email?: string;
  tags: string[];
  company?: string;
}

export interface WhatsAppMessage {
  to: string;
  type: 'text' | 'template' | 'media' | 'location';
  content: string;
  templateId?: string;
  variables?: Record<string, string>;
  mediaUrl?: string;
  caption?: string;
}

export interface WhatsAppResponse {
  success: boolean;
  messageId?: string;
  status?: string;
  error?: string;
}

export interface WhatsAppTag {
  id: string;
  name: string;
  color: string;
  description?: string;
  isActive: boolean;
}

export interface WhatsAppAttendant {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: 'attendant' | 'supervisor' | 'admin';
  isActive: boolean;
}

export interface WhatsAppChatbot {
  id: string;
  isEnabled: boolean;
  config: any;
}

export class WhatsAppIntegrationService {
  // Configuração
  static async getConfig(): Promise<WhatsAppConfig> {
    const response = await api.get('/whatsapp-integration/config');
    return response.data.data;
  }

  static async updateConfig(config: Partial<WhatsAppConfig>): Promise<void> {
    await api.put('/whatsapp-integration/config', config);
  }

  static async getInstanceStatus(): Promise<any> {
    const response = await api.get('/whatsapp-integration/status');
    return response.data.data;
  }

  static async testConnection(): Promise<any> {
    const response = await api.post('/whatsapp-integration/test-connection');
    return response.data.data;
  }

  // Templates
  static async getTemplates(): Promise<MessageTemplate[]> {
    const response = await api.get('/whatsapp-integration/templates');
    return response.data.data.templates;
  }

  static async createTemplate(template: Omit<MessageTemplate, 'id'>): Promise<MessageTemplate> {
    const response = await api.post('/whatsapp-integration/templates', template);
    return response.data.data.template;
  }

  static async updateTemplate(id: string, template: Partial<MessageTemplate>): Promise<MessageTemplate> {
    const response = await api.put(`/whatsapp-integration/templates/${id}`, template);
    return response.data.data.template;
  }

  static async deleteTemplate(id: string): Promise<void> {
    await api.delete(`/whatsapp-integration/templates/${id}`);
  }

  static async previewTemplate(id: string, variables: Record<string, string>): Promise<{ preview: string; missingVariables: string[] }> {
    const response = await api.post(`/whatsapp-integration/templates/${id}/preview`, { variables });
    return response.data.data;
  }

  static async validateTemplate(id: string, variables: Record<string, string>): Promise<{ isValid: boolean; missingVariables: string[] }> {
    const response = await api.post(`/whatsapp-integration/templates/${id}/validate`, { variables });
    return response.data.data;
  }

  // Contatos
  static async getContacts(): Promise<Contact[]> {
    const response = await api.get('/whatsapp-integration/contacts');
    return response.data.data.contacts;
  }

  static async createContact(contact: Omit<Contact, 'id'>): Promise<Contact> {
    const response = await api.post('/whatsapp-integration/contacts', contact);
    return response.data.data.contact;
  }

  static async updateContact(id: string, contact: Partial<Contact>): Promise<Contact> {
    const response = await api.put(`/whatsapp-integration/contacts/${id}`, contact);
    return response.data.data.contact;
  }

  static async deleteContact(id: string): Promise<void> {
    await api.delete(`/whatsapp-integration/contacts/${id}`);
  }

  // Usuários (Atendentes)
  static async getUsers(): Promise<WhatsAppAttendant[]> {
    const response = await api.get('/whatsapp-integration/users');
    return response.data.data.users;
  }

  static async createUser(user: Omit<WhatsAppAttendant, 'id'>): Promise<WhatsAppAttendant> {
    const response = await api.post('/whatsapp-integration/users', user);
    return response.data.data.user;
  }

  static async updateUser(id: string, user: Partial<WhatsAppAttendant>): Promise<WhatsAppAttendant> {
    const response = await api.put(`/whatsapp-integration/users/${id}`, user);
    return response.data.data.user;
  }

  static async deleteUser(id: string): Promise<void> {
    await api.delete(`/whatsapp-integration/users/${id}`);
  }

  // Etiquetas
  static async getTags(): Promise<WhatsAppTag[]> {
    const response = await api.get('/whatsapp-integration/tags');
    return response.data.data.tags;
  }

  static async createTag(tag: Omit<WhatsAppTag, 'id'>): Promise<WhatsAppTag> {
    const response = await api.post('/whatsapp-integration/tags', tag);
    return response.data.data.tag;
  }

  static async updateTag(id: string, tag: Partial<WhatsAppTag>): Promise<WhatsAppTag> {
    const response = await api.put(`/whatsapp-integration/tags/${id}`, tag);
    return response.data.data.tag;
  }

  static async deleteTag(id: string): Promise<void> {
    await api.delete(`/whatsapp-integration/tags/${id}`);
  }

  // Envio de Mensagens
  static async sendMessage(message: WhatsAppMessage): Promise<WhatsAppResponse> {
    const response = await api.post('/whatsapp-integration/messages', message);
    return response.data.data;
  }

  static async sendTextMessage(to: string, content: string): Promise<WhatsAppResponse> {
    const response = await api.post('/whatsapp-integration/messages/text', { to, content });
    return response.data.data;
  }

  static async sendTemplateMessage(
    to: string,
    templateId: string,
    variables: Record<string, string>
  ): Promise<WhatsAppResponse> {
    const response = await api.post('/whatsapp-integration/messages/template', {
      to,
      templateId,
      variables,
    });
    return response.data.data;
  }

  // Integração com Cobrança
  static async sendCobrancaMessage(
    phone: string,
    tituloId: string,
    clienteId: string,
    templateId?: string
  ): Promise<WhatsAppResponse> {
    const response = await api.post('/whatsapp-integration/messages/cobranca', {
      phone,
      tituloId,
      clienteId,
      templateId,
    });
    return response.data.data;
  }

  // Webhook
  static async getWebhookInfo(): Promise<any> {
    const response = await api.get('/whatsapp-integration/webhook');
    return response.data.data;
  }

  static async setupWebhook(url: string): Promise<void> {
    await api.post('/whatsapp-integration/webhook/setup', { url });
  }

  // Chatbot/IA Nativa
  static async getChatbotConfig(): Promise<WhatsAppChatbot> {
    const response = await api.get('/whatsapp-integration/chatbot');
    return response.data.data;
  }

  static async enableChatbot(enabled: boolean): Promise<void> {
    await api.post('/whatsapp-integration/chatbot', { enabled });
  }

  // Estatísticas
  static async getStatistics(period?: string): Promise<any> {
    const params = period ? `?period=${period}` : '';
    const response = await api.get(`/whatsapp-integration/statistics${params}`);
    return response.data.data;
  }

  // Processamento de Variáveis
  static async processVariables(content: string, variables: Record<string, string>): Promise<string> {
    const response = await api.post('/whatsapp-integration/process-variables', {
      content,
      variables,
    });
    return response.data.data.processedContent;
  }
}
